import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useBranch } from "@/contexts/BranchContext";
import { useBranchFreeze } from "@/lib/useBranchFreeze";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  TrendingUp, Plus, Search, MoreHorizontal, Pencil, Trash2, Wallet, AlertCircle,
} from "lucide-react";
import { PAYMENT_MODES, formatMode } from "@/lib/paymentModes";
const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d + (d.length === 10 ? "T00:00:00" : "")).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return d; }
};

const EMPTY_MANUAL = {
  date: new Date().toISOString().slice(0, 10),
  description: "",
  amount: "",
  mode: "cash",
  reference_no: "",
  received_by: "",
  note: "",
};

export default function AccountingIncomePage() {
  const { user } = useAuth();
  const { approvedSchools, allBranches, selectedSchool, selectedBranch } = useBranch();
  const { frozenBranch, lockedBranchId } = useBranchFreeze();
  const currency = user?.currency_symbol || "₱";
  const peso = (n) => `${currency}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Filters
  const [branchFilter, setBranchFilter] = useState(() => lockedBranchId || "all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [category, setCategory] = useState("all");
  const [mode, setMode] = useState("all");
  const [q, setQ] = useState("");

  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_MANUAL);
  const [formBranchId, setFormBranchId] = useState("");
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const branchesForSelectedSchool = useMemo(
    () => allBranches.filter((b) => b.school_id === selectedSchool?.id && b.status === "approved"),
    [allBranches, selectedSchool?.id]
  );

  // Frozen branch users always operate within their assigned branch.
  const effBranchFilter = frozenBranch && lockedBranchId ? lockedBranchId : branchFilter;

  const reload = useCallback(async () => {
    if (!selectedSchool) { setData(null); return; }
    setLoading(true);
    try {
      const params = { school_id: selectedSchool.id };
      if (effBranchFilter !== "all") params.branch_id = effBranchFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (category !== "all") params.category = category;
      if (mode !== "all") params.mode = mode;
      if (q.trim()) params.q = q.trim();
      const { data: res } = await api.get("/accounting/income", { params });
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [selectedSchool, effBranchFilter, dateFrom, dateTo, category, mode, q]);

  useEffect(() => { reload(); }, [reload]);

  const distinctCategories = useMemo(() => {
    const set = new Set();
    (data?.payments_received || []).forEach((r) => r.category && set.add(r.category));
    return [...set].sort();
  }, [data]);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_MANUAL);
    setFormBranchId(selectedBranch?.id || branchesForSelectedSchool[0]?.id || "");
    setOpenForm(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      date: row.date || "",
      description: row.description || "",
      amount: String(row.amount || ""),
      mode: row.mode || "cash",
      reference_no: row.reference_no || "",
      received_by: row.received_by || "",
      note: row.note || "",
    });
    setFormBranchId(row.branch_id);
    setOpenForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!formBranchId) { toast.error("Pick a branch"); return; }
    if (!form.description.trim()) { toast.error("Description is required"); return; }
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error("Amount must be > 0"); return; }
    setBusy(true);
    try {
      const body = {
        branch_id: formBranchId,
        date: form.date,
        description: form.description.trim(),
        amount: amt,
        mode: form.mode,
        reference_no: form.reference_no.trim(),
        received_by: form.received_by.trim(),
        note: form.note.trim(),
      };
      if (editing) {
        delete body.branch_id;
        await api.patch(`/accounting/manual-income/${editing.id}`, body);
        toast.success("Income updated");
      } else {
        await api.post("/accounting/manual-income", body);
        toast.success("Income added");
      }
      setOpenForm(false);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/accounting/manual-income/${deleteTarget.id}`);
      toast.success("Removed");
      setDeleteTarget(null);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <>
      <div className="mb-5">
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight flex items-center gap-2">
          <TrendingUp className="w-7 h-7 text-brand-purple" />
          Income
        </h1>
        <p className="text-brand-inkMuted mt-0.5 text-sm">
          Track all school income from student payments and manual income entries.
        </p>
      </div>

      {!selectedSchool ? (
        <NoSchoolCard />
      ) : (
        <>
          {/* Filters */}
          <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-3 sm:p-4 mb-4" data-testid="income-filters">
            <div className="grid sm:grid-cols-3 gap-3 mb-3">
              <Field label="School">
                <Input value={selectedSchool.name} disabled className="rounded-xl h-10 bg-brand-purpleLight/30" />
              </Field>
              <Field label="Branch">
                <select
                  value={effBranchFilter}
                  onChange={(e) => setBranchFilter(e.target.value)}
                  disabled={frozenBranch}
                  title={frozenBranch ? "Locked to your assigned branch" : undefined}
                  className="w-full h-10 rounded-xl border border-brand-purple/15 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple disabled:bg-brand-purpleLight/30 disabled:cursor-not-allowed disabled:opacity-80"
                  data-testid="income-filter-branch"
                >
                  {!frozenBranch && <option value="all">All branches</option>}
                  {branchesForSelectedSchool.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Search">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-inkMuted" />
                  <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Student, description, ref no., received by"
                    className="rounded-xl h-10 pl-9 border-brand-purple/15"
                    data-testid="income-filter-search"
                  />
                </div>
              </Field>
            </div>
            <div className="grid sm:grid-cols-4 gap-3">
              <Field label="Date From">
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-xl h-10 border-brand-purple/15" data-testid="income-filter-from" />
              </Field>
              <Field label="Date To">
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-xl h-10 border-brand-purple/15" data-testid="income-filter-to" />
              </Field>
              <Field label="Category">
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full h-10 rounded-xl border border-brand-purple/15 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple"
                  data-testid="income-filter-category"
                >
                  <option value="all">All categories</option>
                  {distinctCategories.map((c) => (<option key={c} value={c}>{c}</option>))}
                </select>
              </Field>
              <Field label="Mode of Payment">
                <select
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                  className="w-full h-10 rounded-xl border border-brand-purple/15 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple"
                  data-testid="income-filter-mode"
                >
                  <option value="all">All modes</option>
                  {PAYMENT_MODES.map((m) => (<option key={m.value} value={m.value}>{m.label}</option>))}
                </select>
              </Field>
            </div>
          </section>

          {/* Payment Received */}
          <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm overflow-hidden mb-4" data-testid="payment-received-section">
            <header className="px-4 py-3 border-b border-brand-purple/10 flex items-center gap-2">
              <Wallet className="w-5 h-5 text-brand-purple" />
              <h2 className="font-heading text-lg font-semibold">Payment Received</h2>
            </header>
            {loading ? (
              <div className="p-6 text-brand-inkMuted">Loading...</div>
            ) : (data?.payments_received || []).length === 0 ? (
              <div className="p-8 text-center text-brand-inkMuted text-sm" data-testid="no-payments-received">
                No payments in this window.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F1F5FF] text-brand-inkMuted text-[11px]">
                    <tr>
                      <th className="text-left px-4 py-3 font-heading font-semibold tracking-wider">DATE</th>
                      <th className="text-left px-3 py-3 font-heading font-semibold tracking-wider">STUDENT NAME</th>
                      <th className="text-left px-3 py-3 font-heading font-semibold tracking-wider">CATEGORY</th>
                      <th className="text-left px-3 py-3 font-heading font-semibold tracking-wider">DESCRIPTION</th>
                      <th className="text-right px-3 py-3 font-heading font-semibold tracking-wider">AMOUNT</th>
                      <th className="text-left px-3 py-3 font-heading font-semibold tracking-wider">MODE OF PAYMENT</th>
                      <th className="text-left px-3 py-3 font-heading font-semibold tracking-wider">REFERENCE NO.</th>
                      <th className="text-left px-3 py-3 font-heading font-semibold tracking-wider">RECEIVED BY</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.payments_received.map((r) => (
                      <tr key={`${r.source}-${r.id}`} className="border-t border-brand-purple/5 hover:bg-[#FAFBFE]" data-testid={`payment-row-${r.id}`}>
                        <td className="px-4 py-3 whitespace-nowrap">{formatDate(r.date)}</td>
                        <td className="px-3 py-3">{r.student_name || "—"}</td>
                        <td className="px-3 py-3">{r.category}</td>
                        <td className="px-3 py-3">{r.description || "—"}</td>
                        <td className="px-3 py-3 text-right font-semibold">{peso(r.amount)}</td>
                        <td className="px-3 py-3 capitalize">{formatMode(r.mode)}</td>
                        <td className="px-3 py-3 font-mono text-xs">{r.reference_no || "—"}</td>
                        <td className="px-3 py-3">{r.received_by || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <footer className="px-4 py-3 bg-[#F8FAFF] border-t border-brand-purple/10 flex justify-end">
              <span className="text-sm text-brand-inkMuted">Total Payment Received:&nbsp;</span>
              <span className="text-sm font-semibold text-brand-purple" data-testid="total-payment-received">
                {peso(data?.totals?.payments_received)}
              </span>
            </footer>
          </section>

          {/* Manual Income */}
          <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm overflow-hidden mb-4" data-testid="manual-income-section">
            <header className="px-4 py-3 border-b border-brand-purple/10 flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-brand-purple" />
                <h2 className="font-heading text-lg font-semibold">Manual Income</h2>
              </div>
              <Button onClick={openAdd} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white h-10 px-5" data-testid="add-income-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Add Income
              </Button>
            </header>
            {(data?.manual_income || []).length === 0 ? (
              <div className="p-8 text-center text-brand-inkMuted text-sm" data-testid="no-manual-income">
                No manual income recorded for this window.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F1F5FF] text-brand-inkMuted text-[11px]">
                    <tr>
                      <th className="text-left px-4 py-3 font-heading font-semibold tracking-wider">DATE</th>
                      <th className="text-left px-3 py-3 font-heading font-semibold tracking-wider">DESCRIPTION</th>
                      <th className="text-right px-3 py-3 font-heading font-semibold tracking-wider">AMOUNT</th>
                      <th className="text-left px-3 py-3 font-heading font-semibold tracking-wider">MODE OF PAYMENT</th>
                      <th className="text-left px-3 py-3 font-heading font-semibold tracking-wider">REFERENCE NO.</th>
                      <th className="text-left px-3 py-3 font-heading font-semibold tracking-wider">RECEIVED BY</th>
                      <th className="text-center px-3 py-3 font-heading font-semibold tracking-wider">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.manual_income.map((r) => (
                      <tr key={r.id} className="border-t border-brand-purple/5 hover:bg-[#FAFBFE]" data-testid={`manual-income-row-${r.id}`}>
                        <td className="px-4 py-3 whitespace-nowrap">{formatDate(r.date)}</td>
                        <td className="px-3 py-3">{r.description}</td>
                        <td className="px-3 py-3 text-right font-semibold">{peso(r.amount)}</td>
                        <td className="px-3 py-3">{formatMode(r.mode)}</td>
                        <td className="px-3 py-3 font-mono text-xs">{r.reference_no || "—"}</td>
                        <td className="px-3 py-3">{r.received_by || "—"}</td>
                        <td className="px-3 py-3 text-center">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="inline-flex items-center justify-center w-8 h-8 rounded-full text-brand-inkMuted hover:bg-brand-purpleLight" data-testid={`manual-income-actions-${r.id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="rounded-2xl w-36 p-1.5">
                              <DropdownMenuItem onClick={() => openEdit(r)} className="rounded-xl gap-2 cursor-pointer" data-testid={`edit-manual-income-${r.id}`}>
                                <Pencil className="w-4 h-4" /> Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setDeleteTarget(r)} className="rounded-xl gap-2 cursor-pointer text-[#C62828] focus:text-[#C62828]" data-testid={`delete-manual-income-${r.id}`}>
                                <Trash2 className="w-4 h-4" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <footer className="px-4 py-3 bg-[#F8FAFF] border-t border-brand-purple/10 flex justify-end">
              <span className="text-sm text-brand-inkMuted">Total Manual Income:&nbsp;</span>
              <span className="text-sm font-semibold text-brand-purple" data-testid="total-manual-income">
                {peso(data?.totals?.manual_income)}
              </span>
            </footer>
          </section>

          {/* Grand Total */}
          <div className="rounded-2xl border-2 border-brand-purple/30 bg-gradient-to-r from-[#EFF4FF] to-white p-5 flex justify-between items-center" data-testid="grand-total-income">
            <span className="text-xs uppercase tracking-[0.18em] text-brand-inkMuted font-heading">Grand Total Income</span>
            <span className="text-3xl font-heading font-bold text-brand-purple">{peso(data?.totals?.grand_total)}</span>
          </div>
        </>
      )}

      {/* Add/Edit dialog */}
      <Dialog open={openForm} onOpenChange={(o) => { setOpenForm(o); if (!o) setEditing(null); }}>
        <DialogContent className="rounded-3xl border-brand-purple/20 max-w-lg" data-testid="manual-income-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editing ? "Edit Income" : "Add Manual Income"}</DialogTitle>
            <DialogDescription>
              Record any income that doesn't come from a tuition or student account payment (donations, refunds, miscellaneous receipts, etc.)
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            {!editing && (
              <Field label="Branch" required>
                <select
                  required
                  value={formBranchId}
                  onChange={(e) => setFormBranchId(e.target.value)}
                  className="w-full h-11 rounded-2xl border border-brand-purple/20 bg-white px-3 text-sm"
                  data-testid="manual-income-branch"
                >
                  <option value="">Select branch...</option>
                  {branchesForSelectedSchool.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
                </select>
              </Field>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Date" required>
                <Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20" data-testid="manual-income-date" />
              </Field>
              <Field label="Amount" required>
                <Input type="number" step="0.01" min="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20" data-testid="manual-income-amount" />
              </Field>
            </div>
            <Field label="Description" required>
              <Input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20" placeholder="e.g. MONTH OF APRIL - LUKA D." data-testid="manual-income-description" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Mode of Payment">
                <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="w-full h-11 rounded-2xl border border-brand-purple/20 bg-white px-3 text-sm" data-testid="manual-income-mode">
                  {PAYMENT_MODES.map((m) => (<option key={m.value} value={m.value}>{m.label}</option>))}
                </select>
              </Field>
              <Field label="Reference No.">
                <Input value={form.reference_no} onChange={(e) => setForm({ ...form, reference_no: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20" placeholder="B22 00024" data-testid="manual-income-reference" />
              </Field>
            </div>
            <Field label="Received By">
              <Input value={form.received_by} onChange={(e) => setForm({ ...form, received_by: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20" placeholder="Who collected this" data-testid="manual-income-received-by" />
            </Field>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setOpenForm(false)} className="rounded-full border-brand-purple/30">Cancel</Button>
              <Button type="submit" disabled={busy} className="rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white" data-testid="manual-income-submit">
                {busy ? "Saving..." : editing ? "Save changes" : "Add Income"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-3xl border-brand-purple/20" data-testid="delete-manual-income-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl">Delete this entry?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.description}" — {peso(deleteTarget?.amount)} will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full border-brand-purple/30">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="rounded-full bg-[#C62828] hover:bg-[#B71C1C] text-white" data-testid="confirm-delete-manual-income">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="font-heading text-sm">{label} {required && <span className="text-[#C62828]">*</span>}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function NoSchoolCard() {
  return (
    <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 text-center max-w-xl" data-testid="no-school-picked">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FFF3E0] flex items-center justify-center mb-3">
        <AlertCircle className="w-7 h-7 text-[#E65100]" />
      </div>
      <p className="font-heading text-lg font-semibold text-foreground">Pick a school first</p>
      <p className="text-brand-inkMuted mt-1 text-sm">
        Accounting is scoped per school. Use the school/branch dropdown in the header.
      </p>
    </div>
  );
}
